import React, { useState } from 'react';
import { MapPin, Send, Calendar, Sparkles, Mail, Instagram, Facebook } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { CONTACT, SOCIALS } from '../mock';
import { submitContact } from '../api';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setBusy(true);
    try {
      await submitContact(form);
      toast({ title: 'Message sent \u2728', description: 'Thank you for reaching out. We will reply within 1\u20132 business days.' });
      setForm({ name: '', email: '', message: '' });
    } catch {
      toast({ title: 'Something went wrong', description: 'Please try again in a moment.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-28 pb-24 min-h-screen">
      <div className="max-w-6xl mx-auto px-5">
        <div className="text-center mb-14">
          <p className="font-jost text-xs tracking-[0.4em] text-[#ff4da6] uppercase mb-3">We'd Love To Hear From You</p>
          <h1 className="font-cinzel text-4xl md:text-6xl text-gold-gradient">Get In Touch</h1>
          <div className="gold-divider mt-4" />
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          <div className="space-y-6">
            <p className="font-cormorant text-2xl text-[#f5ecd9]/80">Questions about your ritual, an order, or a session? Send us a message and we'll respond with intention.</p>
            {[
              { icon: Mail, t: 'Email', d: CONTACT.email },
              { icon: Calendar, t: 'Book a Healing', d: 'Reserve a session anytime via our Book page' },
              { icon: MapPin, t: 'In-Person Sessions', d: 'By appointment · location shared upon confirmed booking' },
            ].map((c) => (
              <div key={c.t} className="flex items-center gap-4 card-glass rounded-xl p-5">
                <div className="h-12 w-12 rounded-full border border-[#d4af37]/30 flex items-center justify-center flex-shrink-0"><c.icon className="h-5 w-5 text-[#f0d582]" /></div>
                <div>
                  <p className="font-jost text-[10px] uppercase tracking-widest text-[#f5ecd9]/40">{c.t}</p>
                  <p className="font-cormorant text-lg text-[#f5ecd9]">{c.d}</p>
                </div>
              </div>
            ))}
            <div className="flex items-center gap-4 pt-2">
              <span className="font-jost text-[10px] uppercase tracking-widest text-[#f5ecd9]/40">Follow us</span>
              {[{ Icon: Instagram, href: SOCIALS.instagram }, { Icon: Facebook, href: SOCIALS.facebook }].map(({ Icon, href }, i) => (
                <a key={i} href={href} target="_blank" rel="noreferrer" className="h-10 w-10 rounded-full border border-[#d4af37]/30 flex items-center justify-center text-[#f0d582] hover:bg-[#d4af37]/10 hover:scale-110 transition-all duration-300"><Icon className="h-4 w-4" /></a>
              ))}
            </div>
          </div>

          <form onSubmit={submit} className="card-glass rounded-2xl p-8 space-y-5">
            <div><label className="font-jost text-xs uppercase tracking-widest text-[#f5ecd9]/60">Name</label><input data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 w-full bg-white/5 border border-[#d4af37]/30 rounded-md px-4 py-3 text-sm text-[#f5ecd9] focus:outline-none focus:border-[#ff4da6] transition-colors" /></div>
            <div><label className="font-jost text-xs uppercase tracking-widest text-[#f5ecd9]/60">Email</label><input type="email" data-testid="contact-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 w-full bg-white/5 border border-[#d4af37]/30 rounded-md px-4 py-3 text-sm text-[#f5ecd9] focus:outline-none focus:border-[#ff4da6] transition-colors" /></div>
            <div><label className="font-jost text-xs uppercase tracking-widest text-[#f5ecd9]/60">Message</label><textarea rows={5} data-testid="contact-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2 w-full bg-white/5 border border-[#d4af37]/30 rounded-md px-4 py-3 text-sm text-[#f5ecd9] focus:outline-none focus:border-[#ff4da6] transition-colors resize-none" /></div>
            <button type="submit" disabled={busy} data-testid="contact-submit-btn" className="btn-gold w-full flex items-center justify-center gap-2 rounded-md py-3.5 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] via-[#f0d582] to-[#d4af37] disabled:opacity-60"><Send className="h-4 w-4" /> {busy ? 'Sending…' : 'Send Message'}</button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact;
